export const UtmeSubjects = [
    {
        subject: "Use of English",
        timeAlloted: 2850, //in seconds
        description: `This is a comprehensive assessment of the candidate's proficiency in the Use of English, designed to test both language competence and communication skills.
        
        Exam Structure and Content:
        - Total Questions: 60 multiple-choice questions.
        - Total Score: The raw score is approximately 105 marks, which JAMB standardizes to a final score out of 100.
        - Question Distribution & Scoring:
        1.  Comprehension Passages: 2-3 passages with 10-15 questions in total. Each question carries 3 marks.
        2.  Cloze Test: One passage with 10 questions. Each question carries 1 mark.
        3.  Sentence Completion & Interpretation: 10 questions. Each question carries 2 marks.
        4.  Vocabulary (Antonyms & Synonyms): 15-20 questions. Each question carries 1 mark.
        5.  Oral Forms & Grammar: 10 questions focusing on vowels, consonants, rhymes, and stress patterns. Each question carries 1 mark.

        Key Topics Covered:
        - Comprehension and Summary: Reading for meaning, identifying main ideas, understanding context, and summarizing passages.
        - Lexis and Structure: Grammar rules, sentence patterns, vocabulary, and idiomatic expressions.
        - Oral Forms: Phonetics, stress, intonation, and rhyme schemes.

        Guidelines for Candidates:
        - Time Management: You have 47 minutes and 30 seconds. Allocate your time wisely across the different sections. A suggested breakdown is: ~15-20 minutes for Comprehension, ~5 minutes for the Cloze Test, ~10 minutes for Sentence Completion, ~10 minutes for Vocabulary, and ~5-7 minutes for Oral Forms.
        - Read Carefully: Pay close attention to the instructions for each section and read all passages and questions thoroughly before answering.
        - Practice: Familiarize yourself with different question types by practicing with past questions.

        Notes for Scorers:
        - Automated Scoring: The test is scored electronically to ensure objectivity and accuracy.
        - No Negative Marking: Marks are not deducted for incorrect answers.
        - Scoring Criteria: Answers must match the provided key exactly. No partial credit is awarded, except where specified in sentence interpretation tasks.`,
    },
    {
        subject: "Mathematics",
        timeAlloted: 1350,
        description: `This section assesses fundamental mathematical concepts and problem-solving abilities.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark, for a total raw score of 40. This is then scaled to a final score out of 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Number and Numeration: Number bases, fractions, decimals, percentages, indices, logarithms, and sequences.
        - Algebra: Polynomials, quadratic equations, simultaneous equations, inequalities, and variation.
        - Geometry and Trigonometry: Plane geometry, solid shapes (mensuration), coordinate geometry, and trigonometric ratios and identities.
        - Calculus: Fundamentals of differentiation and integration.
        - Statistics and Probability: Data representation, measures of central tendency and dispersion, permutations, combinations, and probability theory.

        Guidelines for Candidates:
        - Time Management: With approximately 33 seconds per question, it is crucial to work quickly and accurately.
        - Show Your Work: Although not submitted, working through problems on rough paper can help prevent errors.
        - Use the On-Screen Calculator: Familiarize yourself with the provided on-screen calculator for complex computations.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: There is no penalty for incorrect answers.
        - Precision: Answers must be exact. Where rounding is required, it should typically be to two decimal places unless otherwise specified.`,
    },
    {
        subject: "Physics",
        timeAlloted: 1350,
        description: `This section tests understanding of core principles of physics and their application to solving problems.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question carries 1 mark, totaling 40 raw marks. This score is normalized to 100 for final reporting.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Mechanics: Kinematics, dynamics, work, energy, power, momentum, and equilibrium.
        - Thermal Physics: Temperature, heat, gas laws, and thermodynamics.
        - Waves & Optics: Wave properties, reflection, refraction, interference, diffraction, and polarization.
        - Electricity & Magnetism: Electrostatics, current electricity, circuits, magnetic fields, and electromagnetic induction.
        - Modern Physics: Atomic structure, nuclear physics, radioactivity, and photoelectric effect.

        Guidelines for Candidates:
        - Formulae: Memorize key formulae and understand their applications.
        - Units: Pay close attention to units and ensure consistency in your calculations.
        - Graphs: Practice interpreting graphs and deriving information from them.
        - Time Management: Pace yourself to spend about 33 seconds on each question.

        Notes for Scorers:
        - Automated Scoring: Grading is done electronically.
        - No Negative Marking: Candidates are not penalized for wrong answers.
        - Conceptual and Numerical Questions: The exam includes a mix of questions testing theoretical understanding and numerical problem-solving skills.`,
    },
    {
        subject: "Chemistry",
        timeAlloted: 1350,
        description: `This section evaluates knowledge of chemical principles, reactions, and calculations.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark, for a total of 40 raw marks, which is then reported out of 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Physical Chemistry: Stoichiometry, gas laws, chemical kinetics, equilibrium, and thermochemistry.
        - Inorganic Chemistry: Periodic table trends, chemical bonding, acids, bases, salts, and redox reactions.
        - Organic Chemistry: IUPAC nomenclature, functional groups, isomerism, and basic reaction mechanisms.
        - Environmental and Applied Chemistry: Pollution, water treatment, and industrial chemical processes.

        Guidelines for Candidates:
        - Balancing Equations: Practice balancing chemical equations quickly and accurately.
        - Nomenclature: Be proficient in naming organic and inorganic compounds.
        - Calculations: Master stoichiometric calculations, including mole concepts and concentration.
        - Time Management: Aim to complete each question in about 33 seconds.

        Notes for Scorers:
        - Automated Scoring: The test is graded electronically.
        - No Negative Marking: No marks are deducted for incorrect answers.
        - Accuracy: Correct chemical formulae and adherence to the law of conservation of mass are critical.`,
    },
    {
        subject: "Biology",
        timeAlloted: 1350,
        description: `This section assesses understanding of biological concepts, from the cellular level to entire ecosystems.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark, for a total of 40 raw marks, normalized to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Cell Biology: Cell structure and function, cell division (mitosis and meiosis), and cellular respiration.
        - Genetics: Mendelian genetics, DNA structure and replication, and principles of inheritance.
        - Ecology: Ecosystems, population dynamics, nutrient cycles, and conservation.
        - Anatomy and Physiology: Organ systems in plants and animals, with a focus on humans.
        - Evolution and Biodiversity: Theories of evolution, classification of living things, and characteristics of different kingdoms.

        Guidelines for Candidates:
        - Diagrams: Study and be able to interpret biological diagrams.
        - Terminology: Memorize key biological terms and their definitions.
        - Processes: Understand complex processes like photosynthesis, respiration, and protein synthesis step-by-step.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Syllabus Adherence: Questions and expected answers are strictly based on the JAMB syllabus.`,
    },
    {
        subject: "Agricultural Science",
        timeAlloted: 1350,
        description: `This section covers the principles and practices of agriculture, including crop and animal production.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Crop Production: Soil science, crop husbandry, pest and disease control.
        - Animal Husbandry: Animal breeds and breeding, nutrition, health, and management.
        - Agricultural Economics: Farm management, marketing, and basic accounting.
        - Agricultural Technology: Farm mechanization, biotechnology, and processing.

        Guidelines for Candidates:
        - Practical Knowledge: Relate theoretical knowledge to practical farming scenarios.
        - Terminology: Understand the specific terms used in agriculture.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Standard Units: Answers involving measurements must use standard agricultural units.`,
    },
    {
        subject: "Economics",
        timeAlloted: 1350,
        description: `This section tests understanding of economic theories and their application to real-world issues.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), normalized to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Microeconomics: Demand and supply, consumer behavior, production costs, and market structures.
        - Macroeconomics: National income, inflation, unemployment, and monetary and fiscal policies.
        - International Economics: International trade, balance of payments, and exchange rates.
        - Economic Development: Theories of development, population, and resource allocation.

        Guidelines for Candidates:
        - Graphs and Data: Be proficient in interpreting economic graphs and data tables.
        - Definitions: Memorize key economic concepts and definitions.
        - Current Affairs: Relate economic theories to current events in Nigeria and globally.
        - Time Management: Allocate about 33 seconds for each question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: No marks are deducted for incorrect answers.
        - Standard Conventions: Graph interpretations must follow standard economic conventions.`,
    },
    {
        subject: "Geography",
        timeAlloted: 1350,
        description: `This section assesses knowledge of physical, human, and regional geography, as well as map-reading skills.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Physical Geography: Landforms, climatology, vegetation, and soils.
        - Human Geography: Population, settlement, urbanization, and economic activities.
        - Regional Geography: Focus on Nigeria and Africa.
        - Map Work and Geoinformatics: Map reading, interpretation of topographical maps, and basic remote sensing.

        Guidelines for Candidates:
        - Map Skills: Practice reading and interpreting maps, including scales, symbols, and contours.
        - Case Studies: Be familiar with specific geographical examples, especially within Nigeria.
        - Time Management: Spend approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Map Accuracy: Questions involving maps will be precise, and answers must correspond to the provided materials.`,
    },
    {
        subject: "Literature-in-English",
        timeAlloted: 1350,
        description: `This section evaluates the understanding and analysis of literary texts and concepts.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), reported out of 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Prescribed Texts: Detailed knowledge of the set texts for prose, drama, and poetry (both African and non-African).
        - Literary Analysis: Thematic analysis, characterization, plot, and setting.
        - Literary Devices: Identification and understanding of figures of speech, poetic devices, and dramatic techniques.
        - General Literary Principles: Understanding of different genres and literary periods.

        Guidelines for Candidates:
        - Read the Texts: Thoroughly read and understand all prescribed literary texts.
        - Literary Terms: Memorize and be able to apply literary terms.
        - Context: Pay attention to the context provided in excerpts.
        - Time Management: Allocate about 33 seconds for each question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: No marks are deducted for incorrect answers.
        - Textual Accuracy: Answers must be based on the content of the prescribed texts.`,
    },
    {
        subject: "Government",
        timeAlloted: 1350,
        description: `This section tests knowledge of political systems, institutions, and theories, with a focus on Nigeria.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), normalized to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Political Concepts: Power, authority, legitimacy, and sovereignty.
        - Systems of Government: Unitarism, federalism, presidentialism, and parliamentarianism.
        - Nigerian Government and Politics: Constitutional development, political parties, electoral processes, and public administration.
        - International Relations: Foreign policy, international organizations.

        Guidelines for Candidates:
        - Constitution: Be familiar with the key provisions of the Nigerian Constitution.
        - Current Events: Stay informed about current political developments.
        - Definitions: Understand the definitions of key political terms.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Factual Accuracy: Answers must be factually correct and reflect the Nigerian political context.`,
    },
    {
        subject: "Christian Religious Studies",
        timeAlloted: 1350,
        description: `This section assesses knowledge of the Bible, Christian doctrines, and Church history.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Old Testament: Creation, the Patriarchs, the Exodus, the Law, and the Prophets.
        - New Testament: The life and teachings of Jesus Christ, the history of the early Church, and the Epistles.
        - Church History: Key events and figures in the history of Christianity, particularly in Nigeria.
        - Christian Ethics: Application of Christian teachings to moral issues.

        Guidelines for Candidates:
        - Bible Knowledge: Read and be familiar with the key stories and teachings of the Bible.
        - Key Figures: Know the roles of important biblical characters.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Standard References: Biblical references follow standard notation (Book Chapter:Verse).`,
    },
    {
        subject: "Islamic Religious Studies",
        timeAlloted: 1350,
        description: `This section evaluates knowledge of the Qur'an, Hadith, Islamic law, and history.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), normalized to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Qur'an: Revelation, themes of different Surahs, and principles of Tafsir.
        - Hadith: Classification, key narrators, and teachings.
        - Fiqh (Islamic Jurisprudence): Pillars of Islam, rules of worship, and family law.
        - Islamic History: The life of Prophet Muhammad (SAW), the Rightly Guided Caliphs, and the spread of Islam.

        Guidelines for Candidates:
        - Memorization: Memorize key verses from the Qur'an and important Hadiths.
        - Arabic Terms: Understand the meaning of common Arabic terms.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Transliteration: Arabic terms are transliterated according to standard conventions.`,
    },
    {
        subject: "History",
        timeAlloted: 1350,
        description: `This section tests knowledge of major historical events and developments in Nigeria, Africa, and the world.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Nigerian History: Pre-colonial societies, colonial rule, and post-independence developments.
        - African History: Major empires, the slave trade, decolonization, and contemporary issues.
        - World History: Revolutions, world wars, and the Cold War.
        - Historiography: Understanding historical sources and methods.

        Guidelines for Candidates:
        - Dates and Timelines: Memorize key dates and be able to sequence historical events.
        - Cause and Effect: Understand the causes and consequences of major historical events.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Chronological Accuracy: Answers must adhere to the established historical chronology.`,
    },
    {
        subject: "Commerce",
        timeAlloted: 1350,
        description: `This section assesses understanding of commercial activities, business organization, and finance.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), normalized to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Principles of Commerce: Trade, production, and aids to trade.
        - Business Organizations: Sole proprietorships, partnerships, and limited liability companies.
        - Marketing: The marketing mix, distribution channels, and advertising.
        - Finance and Banking: Financial institutions, capital markets, and insurance.

        Guidelines for Candidates:
        - Business Terminology: Be familiar with common business terms.
        - Practical Examples: Apply commercial principles to real-world business scenarios.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Accuracy: Financial figures and business terminology must be used correctly.`,
    },
    {
        subject: "Principles of Accounts",
        timeAlloted: 1350,
        description: `This section evaluates knowledge of bookkeeping, financial accounting, and reporting.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Bookkeeping: Double-entry principles, journals, ledgers, and the trial balance.
        - Financial Statements: Preparation of the Trading, Profit and Loss Account, and Balance Sheet.
        - Accounting Concepts: Principles, conventions, and ethics.
        - Specialized Accounts: Partnership accounts, company accounts, and non-profit organization accounts.

        Guidelines for Candidates:
        - Practice: Work through numerous accounting problems to master the techniques.
        - Formats: Memorize the standard formats for financial statements.
        - Calculations: Be quick and accurate with your calculations.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Accounting Standards: Answers must conform to standard accounting conventions.`,
    },
    {
        subject: "Hausa",
        timeAlloted: 1350,
        description: `This section assesses proficiency in the Hausa language, including grammar, comprehension, and literature.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Grammar and Syntax (Nahawu): Sentence structure, verb forms, and noun classes.
        - Comprehension (Fahimta): Reading and understanding passages in Hausa.
        - Literature (Adabi): Analysis of Hausa prose, poetry, and drama.
        - Culture (Al'ada): Knowledge of Hausa customs, traditions, and proverbs.

        Guidelines for Candidates:
        - Read Widely: Read various Hausa texts to improve your vocabulary and comprehension.
        - Grammar Rules: Master the rules of Hausa grammar.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Orthography: Standard Hausa spelling and orthography are required.`,
    },
    {
        subject: "Yoruba",
        timeAlloted: 1350,
        description: `This section evaluates proficiency in the Yoruba language, covering grammar, comprehension, and literature.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Grammar and Morphology (Gírámà àti Mọ́fọ́lọ́jì): Tone marks, sentence structure, and word formation.
        - Comprehension (Òye Kíkà): Reading and understanding passages.
        - Literature (Lítíréṣọ̀): Analysis of Yoruba prose, poetry, and drama.
        - Culture (Àṣà): Knowledge of Yoruba customs, proverbs, and traditions.

        Guidelines for Candidates:
        - Tone Marks: Pay close attention to diacritics and tone marks as they affect meaning.
        - Practice Reading: Read Yoruba texts regularly to enhance your skills.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Tonal Accuracy: Correct use of tonal notation is critical for full credit.`,
    },
    {
        subject: "Igbo",
        timeAlloted: 1350,
        description: `This section assesses proficiency in the Igbo language, including grammar, comprehension, and literature.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Grammar (Ụtọasụsụ): Sentence structure, verb conjugations, and noun classes.
        - Comprehension (Ọgụgụ): Reading and understanding passages in Igbo.
        - Literature (Agụmagụ): Analysis of Igbo prose, poetry, and drama.
        - Culture (Omenala): Knowledge of Igbo customs, traditions, and proverbs.

        Guidelines for Candidates:
        - Read Extensively: Improve your vocabulary and comprehension by reading a variety of Igbo texts.
        - Grammar: Master the rules of Igbo grammar and sentence construction.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Standard Orthography: Answers must use the standard Igbo orthography.`,
    },
    {
        subject: "Music",
        timeAlloted: 1350,
        description: `This section tests knowledge of music theory, history, and analysis, with a focus on both Western and African music.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Music Theory: Notation, scales, chords, and harmony.
        - Music History: Composers, periods, and styles of Western classical music.
        - African Music: Traditional instruments, rhythms, and forms, with a focus on Nigerian music.
        - Aural Skills: Aural recognition of intervals, chords, and rhythms (tested via audio clips).

        Guidelines for Candidates:
        - Practice Theory: Work through exercises in music theory.
        - Listen Actively: Listen to a wide range of music to develop your aural skills.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Audio Questions: For aural questions, answers must correspond accurately to the provided audio excerpts.`,
    },
    {
        subject: "Fine Art",
        timeAlloted: 1350,
        description: `This section evaluates knowledge of art history, principles of design, and artistic techniques.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Art History: Nigerian, African, and Western art movements, artists, and works.
        - Principles of Design: Elements and principles of art (e.g., line, color, balance, contrast).
        - Artistic Techniques: Methods and materials used in drawing, painting, sculpture, and crafts.
        - Art Appreciation: Analysis and critique of artworks.

        Guidelines for Candidates:
        - Visual Analysis: Practice analyzing artworks based on their formal qualities and historical context.
        - Know Your Artists: Be familiar with key artists and their major works.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Image-Based Questions: Questions will use high-resolution images, and answers must relate directly to them.`,
    },
    {
        subject: "French",
        timeAlloted: 1350,
        description: `This section assesses proficiency in the French language, including comprehension, grammar, and communication skills.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.
        - Question Distribution:
          1. Comprehension: 1-2 passages with 10-15 questions total.
          2. Grammar: 15-20 questions on verb conjugation, tenses, agreement, and syntax.
          3. Vocabulary: 10 questions on word meaning, synonyms, and antonyms.
          4. Culture: 5 questions on French-speaking countries and traditions.

        Key Topics Covered:
        - Reading Comprehension: Understanding written French texts and answering related questions.
        - Grammar: Mastery of French grammar rules including gender/number agreement, verb tenses (présent, passé composé, imparfait, futur), pronouns, and prepositions.
        - Vocabulary: Everyday vocabulary, idiomatic expressions, and false friends.
        - Oral Comprehension: Listening to audio clips and answering questions (if audio component is included).
        - French Culture: Basic knowledge of French-speaking countries, customs, and traditions.

        Guidelines for Candidates:
        - Verb Conjugation: Practice regular and irregular verb conjugations in all major tenses.
        - Gender Agreement: Pay special attention to noun-adjective gender agreement.
        - Reading Practice: Regularly read French texts to improve comprehension speed.
        - Listening Skills: If audio is included, practice listening to French at normal speaking speed.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Grammar Precision: Answers must demonstrate correct grammatical structures.
        - Accent Marks: Proper use of French diacritics (é, è, ê, ç, etc.) is required where applicable.
        - Cultural Context: For culture questions, answers should reflect mainstream French cultural knowledge.`,
    },
    {
        subject: "Computer Studies",
        timeAlloted: 1350,
        description: `This section assesses fundamental knowledge of computer hardware, software, and applications.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Computer Fundamentals: History of computers, hardware components, and software types.
        - Data Representation: Number systems (binary, octal, hexadecimal) and data organization.
        - Programming Concepts: Algorithms, flowcharts, and basic programming principles.
        - Networking and Internet: Network topologies, internet protocols, and online safety.
        - ICT Applications: Use of word processors, spreadsheets, databases, and presentation software.

        Guidelines for Candidates:
        - Terminology: Understand the definitions of key computing terms.
        - Problem Solving: Practice creating simple algorithms and flowcharts.
        - Time Management: Allocate approximately 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: Grading is electronic.
        - No Negative Marking: Incorrect answers do not result in a penalty.
        - Pseudocode: Programming questions will use a simplified, language-agnostic pseudocode.`,
    },
    {
        subject: "Physical and Health Education",
        timeAlloted: 1350,
        description: `This section evaluates knowledge of human anatomy, sports science, and principles of healthy living.

        Exam Structure and Content:
        - Total Questions: 40 multiple-choice questions.
        - Total Score: Each question is worth 1 mark (total 40 raw marks), scaled to 100.
        - Time Allotted: 22 minutes and 30 seconds.

        Key Topics Covered:
        - Anatomy and Physiology: The skeletal, muscular, circulatory, and respiratory systems.
        - Health Education: Nutrition, personal hygiene, communicable and non-communicable diseases.
        - Sports Science: Principles of training, biomechanics, and sports psychology.
        - First Aid and Safety: Procedures for common injuries and safety in sports.

        Guidelines for Candidates:
        - Diagrams: Be able to identify parts of the human body from diagrams.
        - Health Principles: Understand the principles of a balanced diet and healthy lifestyle.
        - Time Management: Spend about 33 seconds per question.

        Notes for Scorers:
        - Automated Scoring: The exam is graded electronically.
        - No Negative Marking: No penalty for wrong answers.
        - Diagram Accuracy: Answers related to diagrams must accurately label the specified parts.`,
    }
];

export const UtmeSubjectNames = [
    "Use of English",
    "Mathematics",
    "Physics",
    "Chemistry",
    "Biology",
    "Agricultural Science",
    "Economics",
    "Geography",
    "Literature-in-English",
    "Government",
    "Christian Religious Studies",
    "Islamic Religious Studies",
    "History",
    "Commerce",
    "Principles of Accounts",
    "Hausa",
    "Yoruba",
    "Igbo",
    "Music",
    "Fine Art",
    "French",
    "Computer Studies",
    "Physical and Health Education"
];